(ns assemble.util
  (:require [clojure.string :as str]
            [lemonpi.domain.interface.asset :as domain.asset]
            [linked.core :as l]
            [uix.core :as uix]))

(defn modifier? [key] (contains? #{"Alt" "Meta" "Shift" "Control"} key))

(uix/defhook use-keyboard-listener [mappings]
  (let [[keys set-keys] (uix/use-state #{})
        on-key-up (uix/use-callback (fn [e]
                                      (set-keys #(disj % (.-key e))))
                                    [])

        on-key-down (uix/use-callback (fn [e]
                                        (set-keys #(conj % (.-key e)))

                                        (when-not (modifier? (.-key e))
                                          (on-key-up e)))
                                      [on-key-up])]
    (uix/use-effect (fn []
                      (.addEventListener js/document "keydown" on-key-down)
                      (.addEventListener js/document "keyup" on-key-up)

                      (fn []
                        (.removeEventListener js/document "keyup" on-key-up)
                        (.removeEventListener js/document "keydown" on-key-down)))
                    [on-key-down on-key-up])
    (uix/use-effect (fn []
                      (when-let [f (get mappings keys)]
                        (f)))
                    [mappings keys])))

(defn split-mime-type [mime-type]
  (when mime-type
    (let [[type subtype] (str/split mime-type "/")]
      {:type type
       :subtype subtype})))

(defn split-url [url]
  (when-let [parts (and url (str/split url "/"))]
    {:start (str/join "/" (pop parts))
     :end (peek parts)}))

(defn unsupported-mime-type? [mime-type]
  (= mime-type "unsupported-file-type"))

;; https://github.com/metosin/komponentit/blob/master/src/cljs/komponentit/filepicker.cljs#L8
(def file-size-default-locale-opts-kilobyte {:maximumFractionDigits 0})
(def file-size-default-locale-opts-megabyte {:maximumFractionDigits 1})
(def file-size-default-locale-opts-gigabyte {:maximumFractionDigits 2})

(defn format-file-size
  "Check NumberFormat options for e.g. rounding options:
  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#options"
  ([bytes]
   (format-file-size bytes nil))
  ([bytes {:keys [locale-opts locale iec?]}]
   (let [units (if iec?
                 ["bytes" "KiB" "MiB" "GiB" "TiB" "PiB"]
                 ["bytes" "KB" "MB" "GB" "TB" "PB"])
         m (if iec? 1024 1000)
         unit (if (zero? bytes)
                0
                (js/Math.floor (/ (js/Math.log bytes) (js/Math.log m))))
         size (/ bytes (js/Math.pow m unit))
         file-size-default-locale-opts* (case unit
                                          1 file-size-default-locale-opts-kilobyte
                                          2 file-size-default-locale-opts-megabyte
                                          3 file-size-default-locale-opts-gigabyte
                                          file-size-default-locale-opts-kilobyte)]
     (str (.toLocaleString size (or locale js/undefined) (clj->js (merge file-size-default-locale-opts*
                                                                         locale-opts)))
          " " (get units unit)))))

;; Declare groups of different file types
;; Not a function, should run when JS is parsed
;; Supported extensions come from domain.asset/supported-file-types
(def file-types
  (let [type-config {:image {;; Controls the use of thumbnail on the table.
                             :use-thumbnail true
                             ;; Controls the preview on asset information drawer.
                             ;; Currently either :img or :video
                             :use-preview :img
                             ;; Currently used for upload modal table, which doesn't
                             ;; use thumbnail.
                             :icon "image"}
                     :video {:use-thumbnail true
                             :use-preview :video
                             :icon "videocam"}
                     :audio {:use-thumbnail false
                             ;; <video> can also handle audio files
                             :use-preview :video
                             :icon "music_note"}
                     :model {:use-thumbnail false
                             :use-preview false
                             :icon "view_in_ar"}
                     :fonts {:use-thumbnail false
                             :use-preview :font
                             :icon "text_fields"}
                     :code {:use-thumbnail false
                            :use-preview false
                            :icon "code"}}]
    (->> (mapv (fn [[k v]]
                 [k (->> (get domain.asset/supported-file-types k)
                         (assoc v :exts))]) type-config)
         (into {}))))

;; One set of allowed file-extensions
(def upload-allowed-file-extensions
  (->> (dissoc file-types :fonts)
       (mapcat (comp :exts val))
       (into #{})))

;; Create mapping from extension to group key
(def extension->group
  (reduce (fn [acc [group-id group]]
            (reduce (fn [acc ext]
                      (assoc acc ext group-id))
                    acc
                    (:exts group)))
          {}
          file-types))

(defn file-extension
  [filename]
  (let [parts (str/split filename ".")]
    ;; If we only have one part, there is no extension
    (when (seq (rest parts))
      (str/lower-case (last parts)))))

(defn basename
  "Remove the file extenstion (the last dot and chars after that)"
  [filename]
  (when filename
    (str/replace filename #"\.[^.]*$" "")))

(defn allowed-file-extension? [filename]
  ;; We could also check the mime-type, but old code had the previous
  ;; file-extensions list already available.
  ;; In some cases (3d models?) the mime-types might be more unrealiable?
  (contains? upload-allowed-file-extensions (file-extension filename)))

(defn get-file-type-parameters [filename]
  (let [ext (file-extension filename)
        group-id (extension->group ext)]
    (get file-types group-id)))

(def font-styles
  (l/map "" "Normal"
         "italic" "Italic"))

(defn format-font-style [style]
  (when style
    (or (get font-styles style)
        (str "Unknown: " style))))

(def font-weights
  (l/map 100 "100 Thin"
         200 "200 Extra/Ultra Light"
         300 "300 Light"
         400 "400 Normal/Regular"
         500 "500 Medium"
         600 "600 Semi/Demi Bold"
         700 "700 Bold"
         800 "800 Extra/Ultra Bold"
         900 "900 Black"
         ;; 950 "Extra Black 950"
         ))

(defn format-font-weight [weight]
  (when weight
    (or (get font-weights weight)
        (str "Unknown: " weight))))

(def font-weight-patterns
  ;; Order matters, so "xlight" is matched before "light".
  ;; Match for "xtra" instead of "extra" to match both with one rule.
  ;; For "bold" also add rule to match "bd".
  [["thin" 100]
   ["xlight" 200]
   ["xtralight" 200]
   ["xtralt" 200]
   ["ultralight" 200]
   ["ultlt" 200]
   ["light" 300]
   ["normal" 400]
   ["regular" 400]
   ["book" 400]
   ["roman" 400]
   ["medium" 500]
   ["semibold" 600]
   ["semibd" 600]
   ["demibold" 600]
   ["demibd" 600]
   ["xtrabold" 800]
   ["xtrabd" 800]
   ["ultrabold" 900]
   ["ultrabd" 900]
   ["bold" 700]
   ["bd" 700]
   ["black" 900]
   ["heavy" 900]
   ["ultra" 900]

   ["100" 100]
   ["200" 200]
   ["300" 300]
   ["400" 400]
   ["500" 500]
   ["600" 600]
   ["700" 700]
   ["800" 800]
   ["900" 900]

   ;; Low priority can match other words.
   ["lt" 300]

   ;; Standard should be the least important, as it might be part of the family
   ;; name. Use only if no other pattern was found.
   ["standard" 400]])

(defn parse-font-filename [filename]
  (let [clean-filename (-> filename
                           (str/lower-case)
                           ;; "-" has to be the first or last char in the
                           ;; group to be used literal, else it creates a
                           ;; character range.
                           (str/replace #"[-_ ]" ""))
        style (if (str/includes? clean-filename "italic")
                "italic"
                "")
        weight (some (fn [[pattern weight]]
                       (when (str/includes? clean-filename pattern)
                         weight))
                     font-weight-patterns)]
    {:font-style style
     :font-weight (or weight 400)}))
